import {
  arrayRemove,
  arrayUnion,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore"
import {
  getDb,
  painDoc,
  painsCollection,
  sessionDoc,
  toPain,
  toSession,
} from "@/lib/firebase/firestore"
import { byCreatedAt } from "@/utils/timestamps"
import { stripUndefined } from "@/utils/firestoreWrite"
import type { Pain, PainInput } from "@/types"

/*
 * Pain points and the vote on them.
 *
 * WHO MAY CALL WHAT — enforced by the Firestore rules as well as by the UI.
 *
 *   Anyone in the session: listPains, addPain, updatePain (their own),
 *                          deletePain (their own), castVote, clearVote
 *   Facilitators:          seedKnownPains, updatePain/deletePain for anyone
 *
 * The facilitator writes pain points too — they are in the room, not just
 * driving it — which is why `authorId` is an opaque id compared for equality
 * rather than a reference into the roster.
 */

export interface AddPainInput {
  text: string
  /** Participant id, or the facilitator's uid. */
  authorId: string
  /** Denormalised so a pain reads correctly after its author has left. */
  authorName: string
}

/**
 * Adds a pain point.
 *
 * No pre-read and no count check: position is creation order, so nothing here
 * depends on how many pains already exist, and two people writing at the same
 * moment cannot collide. `painsPerParticipant` is a limit the WRITER's screen
 * applies — enforcing it here would need a transaction over the whole collection
 * on every keystroke-sized write, to stop a behaviour nobody is attempting.
 */
export async function addPain(sessionId: string, input: AddPainInput): Promise<string> {
  // `doc(collection)` + `setDoc` rather than `addDoc`: same write either way —
  // an auto-id is minted on the client too — but this shape is the one
  // `runTransaction` can also take, which `addDoc` cannot.
  const ref = doc(painsCollection(sessionId))
  await setDoc(ref, {
    text: input.text,
    authorId: input.authorId,
    authorName: input.authorName,
    source: "room",
    voterIds: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

/** Oldest first: array position IS the pain's place on the board. */
export async function listPains(sessionId: string): Promise<Pain[]> {
  const snapshot = await getDocs(painsCollection(sessionId))
  return snapshot.docs
    .map(toPain)
    .filter((p): p is Pain => p !== null)
    .sort(byCreatedAt)
}

export async function updatePain(
  sessionId: string,
  painId: string,
  input: PainInput
): Promise<void> {
  await updateDoc(painDoc(sessionId, painId), {
    ...stripUndefined(input),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Deletes a pain outright rather than flagging it.
 *
 * Its votes go with it, which is the intent: a pain nobody can see must not keep
 * counting towards a total. A job already built from it keeps the id in
 * `painIds` and simply renders one fewer source — better than a job that cannot
 * be opened because one of its origins is gone.
 */
export async function deletePain(sessionId: string, painId: string): Promise<void> {
  await deleteDoc(painDoc(sessionId, painId))
}

/* ---------------------------------------------------------------- the vote */

/**
 * Adds this voter to a pain.
 *
 * `arrayUnion` is applied server-side and is idempotent, so a double-tap costs
 * nothing and two people voting in the same instant both land — where a
 * read-modify-write of a count would silently drop one of them.
 *
 * How many votes someone has left is counted across the pains already in the
 * store, so the limit needs no document of its own and nothing to reset between
 * rounds.
 */
export async function castVote(sessionId: string, painId: string, voterId: string): Promise<void> {
  await updateDoc(painDoc(sessionId, painId), {
    voterIds: arrayUnion(voterId),
    updatedAt: serverTimestamp(),
  })
}

export async function clearVote(sessionId: string, painId: string, voterId: string): Promise<void> {
  await updateDoc(painDoc(sessionId, painId), {
    voterIds: arrayRemove(voterId),
    updatedAt: serverTimestamp(),
  })
}

/* ------------------------------------------------------------------ seeding */

/**
 * Turns the facilitator's loaded notes into real pain points, once.
 *
 * `session.knownPains` is configuration — a list of sentences typed before the
 * room existed. The moment the room can vote, each one needs an id, an author
 * and a place in creation order like any other pain, because a vote is cast
 * against a document.
 *
 * Idempotent by text: it reads the notes already seeded and writes only the ones
 * missing. Without that, a facilitator who steps back into the lobby and forward
 * again would seed the same notes twice and split the room's votes across the
 * duplicates. Text is the identity, not an index, so reordering the notes in the
 * dialog cannot cause a re-seed.
 *
 * Deliberately NOT a transaction: a transaction cannot run a query, so it could
 * not read the collection it needs to compare against — it would only be able to
 * fake the guarantee. The race it would close is two facilitators opening the
 * Pains round in the same second, whose worst outcome is a duplicated note one
 * of them can delete.
 */
export async function seedKnownPains(sessionId: string): Promise<number> {
  const session = await getSessionNotes(sessionId)
  const notes = [...new Set(session.map((note) => note.trim()).filter((note) => note !== ""))]
  if (notes.length === 0) return 0

  const already = new Set(
    (await listPains(sessionId))
      .filter((pain) => pain.source === "known")
      .map((pain) => pain.text.trim())
  )
  const missing = notes.filter((note) => !already.has(note))
  if (missing.length === 0) return 0

  const batch = writeBatch(getDb())
  for (const note of missing) {
    batch.set(doc(painsCollection(sessionId)), {
      text: note,
      authorId: null,
      authorName: "",
      source: "known",
      voterIds: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  }
  await batch.commit()
  return missing.length
}

/** The loaded notes, read straight off the session document. */
async function getSessionNotes(sessionId: string): Promise<string[]> {
  const snapshot = await getDoc(sessionDoc(sessionId))
  const session = toSession(snapshot)
  if (!session) throw new Error(`Session ${sessionId} does not exist`)
  return session.knownPains
}
